import {
  memo,
  HTMLAttributes,
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  forwardRef,
  Ref,
  useImperativeHandle,
  ReactNode,
} from 'react';
import {
  Button,
  Drawer,
  DrawerProps,
  Input,
  Form,
  Tooltip,
  Select,
  Tag,
  Modal,
  message,
} from 'antd';
import { Formik, FieldArray } from 'formik';
import { useTranslate } from '@tolgee/react';
import { css, cx } from '@emotion/css';
import FormField from 'components/FormField';
import {
  CloseCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  LinkOutlined,
  LoadingOutlined,
  UpOutlined,
} from '@ant-design/icons';
import ConditionField from './ConditionField';
import { useLatest, useMount, useRequest, useUpdateEffect } from 'ahooks';
import { getDepartmentList } from 'services/api/dashboard';
import { listDepartmentAndRoles } from 'services/api/department/department';
import { uniqBy, get, filter, includes, set, map, findIndex, cloneDeep } from 'lodash-es';
import {
  ItemFilter,
  ItemGroup,
  ItemGroupFormValue,
  ItemGroupSpu,
  PreviewItemGroupReq,
} from './type';
import introduceBg from '@knowledge/assets/introduce-bg.png';
import {
  FilterBarValueContext,
  FilterBarValueProvider,
} from '@knowledge/knowledges/singleClass/context';
import FilterBar from '@knowledge/knowledges/singleClass/FilterBar';
import { CollapseOperation } from '@knowledge/knowledges/singleClassNew/CollapseOperation';
import { Virtuoso } from 'react-virtuoso';
import { useStoreInfo } from '@knowledge/common/components/ItemSelectV2/useStoreInfo';
import styled from '@emotion/styled';
import SafeImage from 'components/SafeImage';
import {
  countSku,
  getSpuInfoBySelectMap,
  mergeItems,
  splitSku,
  SpuSelectMap,
  ToggleSku,
  ToggleSpu,
  useSelectControl,
} from '@knowledge/common/helpers/sku';
import { searchInItemScope } from '@knowledge/common/helpers/search';
import Empty from 'components/Empty';
import {
  removeUselessFilter,
  validateItemFilter,
  defaultItemGroupFormValue,
  patchDefaultItemGroupFormValue,
  isItemFilterValid,
} from './helper';
import useSmartField from 'hooks/useSmartField';
import { createItemGroup, previewItems, updateItemGroup, fetchStoresByDepts } from './api';
import { getItemsByInternalIds } from '@knowledge/common/cache/spu';
import { stores2storeScopes } from '@knowledge/Activity/share';
import equal from 'react-fast-compare';
import { translateDepartment } from 'views/Assistant/utils';

export interface EditDrawerSelfProps {
  itemGroup?: ItemGroup;
  isItemLoaded?: boolean;
  fromCopy?: boolean;
}
export interface EditDrawerProps extends DrawerProps, EditDrawerSelfProps {
  onRefresh?: () => void;
  existedNames?: string[];
}
export const EditDrawer = memo((props: EditDrawerProps) => {
  const {
    itemGroup,
    fromCopy = false,
    className,
    isItemLoaded = true,
    onRefresh,
    existedNames = [],
    ...restProps
  } = props;
  const { t } = useTranslate();
  const deleteDepartmentTip = useRef(false);
  const mode = itemGroup?.id ? 'edit' : 'add';
  const { data, loading } = useRequest(() => getDepartmentList('product_groups'), {
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });
  // 用户查询所有部门信息，匹配部门id，显示部门名称
  const { data: data2, loading: loading2 } = useRequest(() => listDepartmentAndRoles(), {
    ready: mode === 'edit' && get(itemGroup, 'departmentId.length', 0) > 0,
    refreshDeps: [mode, itemGroup],
    cacheKey: 'listDepartmentAndRoles-itemGroup',
    onError: () => {
      message.error(t('phrase.获取数据失败，请稍后再试'));
    },
  });

  // 对已保存的部门和我当前权限的部门进行合并展示
  const renderDepartments = useMemo(() => {
    const myDepartments = get(data, 'departments', []);
    const ids = get(itemGroup, 'departmentId', []);
    const existDepartments = filter(get(data2, 'departmentAndRoleOfItList', []), (item) => {
      return includes(ids, item.id);
    });
    return uniqBy(
      [
        ...existDepartments.map((each) => {
          return {
            id: each.id,
            name: translateDepartment(each, t),
          };
        }),
        ...myDepartments.map((each) => {
          return {
            id: each.id,
            name: translateDepartment(each, t),
          };
        }),
      ],
      'id',
    );
  }, [data, data2, itemGroup, t]);

  // 部门变更，店铺范围也需要变更
  const {
    loading: loading3,
    runAsync,
    data: stores,
  } = useRequest((ids) => fetchStoresByDepts(ids), {
    manual: true,
  });

  useMount(() => {
    if (get(itemGroup, 'departmentId.length', 0) > 0) {
      runAsync(itemGroup?.departmentId);
    }
  });

  const storeScopes = useMemo(() => {
    return stores2storeScopes(
      map(stores, (each) => {
        const { id, ...rest } = each;
        return {
          ...rest,
          storeId: id,
        };
      }),
    );
  }, [stores]);

  const initialValues = useMemo(() => {
    if (itemGroup) {
      return patchDefaultItemGroupFormValue(itemGroup);
    }
    return defaultItemGroupFormValue;
  }, [itemGroup]);
  const itemAreaRef = useRef<ItemAreaRef>(null!);
  const btnLoading = loading3; // 店铺信息变更的时候选择商品按钮需要loading
  const handleTrySubmit = () => {
    setTimeout(() => {
      itemAreaRef.current.fetchItems();
    });
  };

  useEffect(() => {
    if (isItemLoaded === false) {
      itemAreaRef.current.fetchItems(true);
    }
  }, [isItemLoaded]);

  const { runAsync: createIG } = useRequest((data) => createItemGroup(data), {
    manual: true,
    onSuccess: () => {
      onRefresh?.();
      restProps?.onClose?.(null!);
    },
  });

  const { runAsync: updateIG } = useRequest((data) => updateItemGroup(data), {
    manual: true,
    onSuccess: () => {
      onRefresh?.();
      restProps?.onClose?.(null!);
    },
  });

  const excludeNames = useMemo(() => {
    if (itemGroup && !fromCopy) {
      return existedNames.filter((name) => name !== itemGroup.name);
    }
    return existedNames;
  }, [existedNames, itemGroup, fromCopy]);

  return (
    <Formik<ItemGroupFormValue>
      initialValues={initialValues}
      validate={(values) => {
        const errors: Record<string, any> = {};
        if (!values.name) {
          errors.name = t('请输入商品组名称');
        }
        if (excludeNames?.includes(values.name)) {
          errors.name = t('已存在相同名称的商品组');
        }
        if (get(values, 'departmentId.length', 0) === 0) {
          errors.departmentId = t('请选择适用部门');
        }
        if (get(validateItemFilter(values.itemFilter), 'length', 0) === 0) {
          set(errors, 'itemFilter.0.itemFilterPair.0.keyword', '请输入');
        }
        return errors;
      }}
      onSubmit={(values) => {
        if (mode === 'add') {
          return createIG(removeUselessFilter(values));
        } else {
          return updateIG({
            ...removeUselessFilter(values),
            id: itemGroup?.id,
          });
        }
      }}
    >
      {({ values, handleSubmit, isSubmitting }) => {
        return (
          <Drawer
            title={fromCopy ? t('复制商品组') : mode === 'edit' ? t('编辑商品组') : t('新增商品组')}
            width={1240}
            footer={
              <div className="flex gap-[16px]">
                <Button
                  loading={isSubmitting}
                  type="primary"
                  onClick={() => {
                    handleSubmit();
                  }}
                >
                  {t('phrase.保存')}
                </Button>
                <Button onClick={() => restProps?.onClose?.(null!)}>{t('phrase.取消')}</Button>
              </div>
            }
            className={cx(
              className,
              css`
                .ant-drawer-body {
                  background: #f8f9fa;
                  padding: 16px;
                }
              `,
            )}
            {...restProps}
          >
            <Form layout="vertical" className="h-full">
              <div className="h-full flex gap-[16px] overflow-hidden">
                <div className="basis-[560px] flex-shrink-0 flex flex-col gap-[16px] overflow-auto">
                  <div className="p-[16px] bg-[var(--fett-bg-panel)] flex-shrink-0">
                    <Title text={t('aide.基础配置')} />
                    <div
                      className={cx(
                        'pr-[48px] mt-[16px]',
                        css`
                          .ant-form-item {
                            margin-bottom: 20px;
                            &:last-child {
                              margin-bottom: 0;
                            }
                          }
                        `,
                      )}
                    >
                      <FormField name="name" label={t('商品组名称')} required>
                        {({ field }) => {
                          return (
                            <Input
                              {...field}
                              placeholder={t('请输入商品组名称')}
                              showCount
                              maxLength={60}
                            />
                          );
                        }}
                      </FormField>
                      <FormField name="departmentId" label={t('适用部门')} required>
                        {({ field }) => {
                          const { onChange, onBlur, ...restField } = field;
                          return (
                            <Select
                              onChange={(e) => {
                                onChange(e);
                              }}
                              onDropdownVisibleChange={(open) => {
                                if (open === false) {
                                  handleTrySubmit();
                                  runAsync(field.value);
                                }
                              }}
                              {...restField}
                              mode="multiple"
                              placeholder={t('请选择适用部门')}
                              loading={loading || loading2}
                              tagRender={(props) => {
                                return (
                                  <Tag
                                    closable
                                    onMouseDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClose={(e) => {
                                      e.preventDefault();
                                      if (!deleteDepartmentTip.current) {
                                        deleteDepartmentTip.current = true;
                                        Modal.confirm({
                                          title: t('official_delete_tip'),
                                          content: t('删除后，该部门相关的商品将被移除'),
                                          onOk: () => {
                                            props.onClose(e);
                                            runAsync(field.value);
                                            handleTrySubmit();
                                          },
                                        });
                                      } else {
                                        props.onClose(e);
                                        runAsync(field.value);
                                        handleTrySubmit();
                                      }
                                    }}
                                  >
                                    {props.label}
                                  </Tag>
                                );
                              }}
                            >
                              {renderDepartments.map((item) => {
                                return (
                                  <Select.Option key={item.id} value={item.id}>
                                    {item.name}
                                  </Select.Option>
                                );
                              })}
                            </Select>
                          );
                        }}
                      </FormField>
                    </div>
                  </div>
                  <div className="p-[16px] flex-grow bg-[var(--fett-bg-panel)]">
                    <Title text={t('商品范围')} />
                    <FieldArray name="itemFilter" shouldUpdate={() => true}>
                      {({ push, remove }) => {
                        return (
                          <div className="bg-[var(--fett-erp-card-bg)] pb-[15px] mt-[12px]">
                            <div className="leading-[40px] text-[14px] border-0 border-b border-solid border-[var(--fett-border-default)] px-[16px]">
                              <span className="text-[red]">*</span>
                              <span>{t('添加商品至商品组')}</span>
                            </div>
                            {values.itemFilter.map((itemFilter, index) => {
                              return (
                                <div className="px-[16px] mt-[12px] mb-[12px]" key={index}>
                                  <div className="bg-[var(--fett-bg-panel)]">
                                    <div className="h-[40px] border-0 border-b border-solid border-[var(--fett-border-default)] flex justify-between px-[16px] items-center">
                                      <span className="text-[var(--fett-text-tertiary)]">
                                        {t('条件x', { x: index + 1 })}
                                      </span>
                                      <Tooltip title={t('marketing.删除')}>
                                        <DeleteOutlined
                                          className={cx({
                                            'cursor-pointer': values.blacklist.length > 1,
                                            '!text-[#d9d9d9] !cursor-not-allowed':
                                              values.itemFilter.length === 1,
                                          })}
                                          onClick={() => {
                                            if (values.itemFilter.length > 1) {
                                              remove(index);
                                              if (isItemFilterValid(itemFilter)) {
                                                handleTrySubmit();
                                              }
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    </div>

                                    <div className="px-[16px] pt-[12px] pb-[16px]">
                                      <div>{t('添加方式')}</div>
                                      <ConditionField
                                        btnLoading={btnLoading}
                                        index={index}
                                        prefix="itemFilter"
                                        storeScopes={storeScopes}
                                        trySubmit={() => {
                                          handleTrySubmit();
                                        }}
                                      />
                                      <div className="text-[12px] text-[var(--fett-text-tertiary)] mt-[8px]">
                                        {t('后续会对符合条件的商品将自动加入商品组')}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            <Button
                              disabled={values.itemFilter.length === 5}
                              type="link"
                              onClick={() => {
                                push({
                                  target: 'SELLER_SKU',
                                  itemFilterPair: [
                                    {
                                      keyword: '',
                                      condition: 'START_WITH',
                                    },
                                  ],
                                  specifiedItem: [],
                                });
                              }}
                              className="py-[2px] mt-[-12px] !text-[var(--fett-text-secondary)] hover:!text-[#3366ff]"
                            >
                              +{t('添加条件')}
                            </Button>
                          </div>
                        );
                      }}
                    </FieldArray>
                    <FieldArray name="blacklist" shouldUpdate={() => true}>
                      {({ push, remove }) => {
                        return (
                          <div className="bg-[var(--fett-erp-card-bg)] pb-[15px] mt-[12px] last:mb-[12px]">
                            <div className="leading-[40px] text-[14px] border-0 border-b border-solid border-[var(--fett-border-default)] px-[16px]">
                              <span>{t('从商品组删除商品')}</span>
                            </div>
                            {values.blacklist.map((_, index) => {
                              return (
                                <div
                                  className="px-[16px] mt-[12px] mb-[12px] last:mb-0"
                                  key={index}
                                >
                                  <div className="bg-[var(--fett-bg-panel)]">
                                    <div className="h-[40px] border-0 border-b border-solid border-[var(--fett-border-default)] flex justify-between px-[16px] items-center">
                                      <span className="text-[var(--fett-text-tertiary)]">
                                        {t('条件x', { x: index + 1 })}
                                      </span>
                                      <Tooltip title={t('marketing.删除')}>
                                        <DeleteOutlined
                                          className="cursor-pointer"
                                          onClick={() => {
                                            remove(index);
                                            if (isItemFilterValid(values.blacklist[index])) {
                                              handleTrySubmit();
                                            }
                                          }}
                                        />
                                      </Tooltip>
                                    </div>

                                    <div className="px-[16px] pt-[12px] pb-[16px]">
                                      <div>{t('删除方式')}</div>
                                      <ConditionField
                                        storeScopes={storeScopes}
                                        givenType="delete"
                                        reverseType
                                        prefix="blacklist"
                                        index={index}
                                        trySubmit={() => {
                                          handleTrySubmit();
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            <Button
                              disabled={values.blacklist.length === 5}
                              type="link"
                              onClick={() => {
                                push({
                                  target: 'PLATFORM_SKU',
                                  itemFilterPair: [
                                    {
                                      keyword: '',
                                      condition: 'START_WITH',
                                    },
                                  ],
                                  specifiedItem: [],
                                });
                              }}
                              className="py-[2px] mt-[-12px] !text-[var(--fett-text-secondary)] hover:!text-[#3366ff]"
                            >
                              +{t('添加条件')}
                            </Button>
                          </div>
                        );
                      }}
                    </FieldArray>
                  </div>
                </div>
                <div className="basis-[602px] flex-grow bg-[var(--fett-bg-panel)] flex flex-col p-[16px]">
                  <FilterBarValueProvider>
                    <ItemArea
                      ref={itemAreaRef}
                      searchParams={{
                        itemFilter: values.itemFilter,
                        blacklist: values.blacklist,
                        departmentId: values.departmentId,
                      }}
                      defaultItems={itemGroup?.selectedItem}
                      storeScopes={storeScopes}
                    />
                  </FilterBarValueProvider>
                </div>
              </div>
            </Form>
          </Drawer>
        );
      }}
    </Formik>
  );
});

interface ItemAreaProps {
  searchParams: Omit<PreviewItemGroupReq, 'cursor'>;
  defaultItems?: ItemGroupSpu[];
  storeScopes?: General.StoreScope[];
}

interface ItemAreaRef {
  getItems: () => ItemSelectV2.Item[];
  fetchItems: (force?: boolean) => Promise<unknown>;
}
const ItemArea = memo(
  forwardRef((props: ItemAreaProps, ref: Ref<ItemAreaRef>) => {
    const { t } = useTranslate();
    const { searchParams, defaultItems, storeScopes: largeStoreScope } = props;
    const previousSearchParams = useRef<typeof searchParams>(searchParams);
    const [items, setItems] = useState<ItemSelectV2.Item[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      if (defaultItems) {
        setLoading(true);
        getItemsByInternalIds(
          map(defaultItems, (each) => {
            return {
              itemInternalId: each.itemInternalId,
              skus: map(each.sku, (sku) => {
                return {
                  skuId: sku.skuId,
                };
              }),
            };
          }),
        )
          .then((res) => {
            setItems(res);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    }, [defaultItems]);
    const itemsRef = useLatest(items);
    const [filterSpus, setFilterSpus] = useState<ItemSelectV2.Item[]>(items);
    const { storeScopes, keyword, keywordType } = useContext(FilterBarValueContext);
    useEffect(() => {
      searchInItemScope(items, {
        storeScopes,
        keyword,
        keywordType,
        name: keywordType === 1 ? keyword : '',
      }).then((res: any) => {
        setFilterSpus(res);
      });
    }, [items, storeScopes, keyword, keywordType]);

    const [foldState, setFoldState] = useState<'fold' | 'unfold'>('fold');
    const cursor = useRef('');
    const { runAsync, cancel } = useRequest(
      (searchParams: PreviewItemGroupReq) => previewItems(removeUselessFilter(searchParams)),
      {
        manual: true,
        debounceWait: 500,
        onError: () => {
          message.error(t('phrase.获取数据失败，请稍后再试'));
          setLoading(false);
        },
        onSuccess: (data) => {
          const res = data?.data;
          if (!!res.nextCursor) {
            cursor.current = res.nextCursor;
            runAsync({
              ...searchParams,
              cursor: res.nextCursor,
            });
          } else {
            setLoading(false);
          }
          getItemsByInternalIds(
            map(res.selectedItem, (item) => {
              return {
                itemInternalId: item.itemInternalId,
                skus: map(item.sku, (sku) => {
                  return {
                    skuId: sku.skuId,
                  };
                }),
              };
            }),
          ).then((res) => {
            setItems([...items, ...res]);
          });
        },
      },
    );
    const noEffectFilter = useMemo(() => {
      const { departmentId, itemFilter } = searchParams;
      if (get(departmentId, 'length', 0) === 0) {
        return true;
      }
      if (get(validateItemFilter(itemFilter), 'length', 0) === 0) {
        return true;
      }
      return false;
    }, [searchParams]);
    const [spuSelectMap, , { toggleSku, toggleSpu }] = useSelectControl(items);

    const { field: blacklistField, helpers } = useSmartField<ItemFilter[]>('blacklist');
    const blacklistFieldRef = useLatest(blacklistField);

    useUpdateEffect(() => {
      // 右侧删除商品的时候需要修改配置，但是不触发重新加载
      const spus = getSpuInfoBySelectMap(itemsRef.current, spuSelectMap);
      if (spus.length === 0) return;
      const blacklist = blacklistFieldRef.current?.value || [];
      const findExistIndex = findIndex(blacklist, (item) => {
        return item.target === 'PLATFORM_SKU';
      });
      if (findExistIndex > -1) {
        const findExist = blacklist[findExistIndex];
        const copy = [...findExist.specifiedItem];
        const partA = map(copy, (item) => {
          const { sku, itemInternalId } = item;
          return {
            itemInternalId,
            skus: sku,
          };
        });
        const partB = map(spus, (item) => {
          return {
            itemInternalId: item.internalItemId,
            skus: map(item.skuInfo, (sku) => {
              return {
                skuId: sku.skuId,
                sellerSku: sku.sellerSku,
              };
            }),
          };
        });
        const merged = mergeItems(partA, partB);
        const newBlacklist = cloneDeep(blacklist);
        newBlacklist.splice(findExistIndex, 1, {
          ...findExist,
          specifiedItem: map(merged, (item) => {
            return {
              itemInternalId: item.itemInternalId,
              sku: map(item.skus, (sku) => {
                return {
                  skuId: sku.skuId,
                  sellerSku: sku.sellerSku,
                };
              }),
            };
          }),
        });
        helpers.setValue(newBlacklist);
      } else {
        helpers.setValue([
          ...blacklist,
          {
            target: 'PLATFORM_SKU',
            itemFilterPair: [
              {
                keyword: '',
                condition: 'START_WITH',
              },
            ],
            specifiedItem: map(spus, (item) => {
              return {
                itemInternalId: item.internalItemId,
                sku: map(item.skuInfo, (sku) => {
                  return {
                    skuId: sku.skuId,
                    sellerSku: sku.sellerSku,
                  };
                }),
              } as ItemGroupSpu;
            }),
          },
        ]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spuSelectMap]);

    useImperativeHandle(
      ref,
      () => ({
        getItems: () => {
          return getSpuInfoBySelectMap(items, spuSelectMap);
        },
        fetchItems: (force = false) => {
          if (noEffectFilter) {
            previousSearchParams.current = searchParams;
            return Promise.resolve();
          }
          if (!force && equal(previousSearchParams.current, searchParams)) {
            previousSearchParams.current = searchParams;
            return Promise.resolve();
          }
          previousSearchParams.current = searchParams;
          setItems([]);
          cancel();
          cursor.current = '';
          setLoading(true);
          return runAsync({
            ...searchParams,
            cursor: cursor.current,
          });
        },
      }),
      [
        items,
        spuSelectMap,
        searchParams,
        noEffectFilter,
        runAsync,
        cursor,
        cancel,
        previousSearchParams,
      ],
    );

    const actualItems = useMemo(() => {
      return splitSku(items, spuSelectMap);
    }, [items, spuSelectMap]);

    useEffect(() => {
      if (noEffectFilter) {
        setItems([]);
        cursor.current = '';
      }
    }, [noEffectFilter]);

    const filterSpuExclude = useMemo(() => {
      return splitSku(filterSpus, spuSelectMap);
    }, [filterSpus, spuSelectMap]);

    return (
      <>
        <Title
          text={
            <div>
              {t('商品组商品')}
              {actualItems.length > 0 && (
                <span className="text-[var(--fett-text-tertiary)] ml-[8px] font-normal text-[14px]">
                  SPU({actualItems.length})&nbsp;SKU({countSku(actualItems, 'skuInfo')})
                </span>
              )}
            </div>
          }
          className="flex-shrink-0"
        />
        <div className="flex-grow">
          {noEffectFilter ? (
            <div className="h-full flex items-center justify-center flex-col">
              <img src={introduceBg} width={120} alt="empty" />
              <div className="text-[var(--fett-text-tertiary)] mt-[12px]">
                {t('请在左侧添加商品')}
              </div>
            </div>
          ) : loading && items.length === 0 ? (
            <div className="h-full flex items-center justify-center flex-col">
              <LoadingOutlined />
              <div className="text-[var(--fett-text-tertiary)] mt-[12px]">
                {t('正在查找符合条件的商品')}
              </div>
            </div>
          ) : (
            <div className="h-full overflow-hidden flex flex-col">
              <div className="flex items-center justify-between pb-[16px] pt-[12px] flex-shrink-0">
                <FilterBar
                  className={css`
                    & > div {
                      &:nth-child(2) {
                        width: 220px !important;
                      }
                    }
                  `}
                  storeSelectPlacement="bottomRight"
                  storeScopes={largeStoreScope}
                />
                <CollapseOperation status={foldState} setStatus={setFoldState} />
              </div>

              <div className="flex-grow overflow-y-auto">
                {filterSpuExclude.length === 0 ? (
                  <div className="h-full flex items-center justify-center">
                    <Empty />
                  </div>
                ) : (
                  <Virtuoso
                    data={filterSpuExclude}
                    itemContent={(_, data) => {
                      return (
                        <ItemRender
                          className={css`
                            box-sizing: border-box;
                            border: 1px solid var(--fett-border-strong);
                            border-radius: 2px;
                            padding: 10px 13px 12px 8px;
                            margin-bottom: 16px;
                          `}
                          foldState={foldState}
                          selectedInfo={spuSelectMap[data.internalItemId] || {}}
                          toggleSku={toggleSku}
                          toggleSpu={toggleSpu}
                          item={data}
                          key={data.internalItemId}
                        />
                      );
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {loading && items.length > 0 && (
          <div className="flex flex-shrink-0 h-[40px] bg-[var(--fett-surface-warning-bg)] text-[#232428] font-medium items-center justify-center">
            <LoadingOutlined className="mr-[6px]" />
            {t('正在查找符合条件的商品')}
          </div>
        )}
      </>
    );
  }),
);

ItemArea.displayName = 'ItemArea';

interface ItemRenderProps extends HTMLAttributes<HTMLDivElement> {
  item: ItemSelectV2.Item;
  foldState: 'fold' | 'unfold';
  selectedInfo: SpuSelectMap[string];
  toggleSku: ToggleSku;
  toggleSpu: ToggleSpu;
}
const ItemRender = memo((props: ItemRenderProps) => {
  const { item, toggleSku, foldState, toggleSpu, className, selectedInfo, ...rest } = props;
  const [open, setOpen] = useState(foldState);
  useUpdateEffect(() => {
    setOpen(foldState);
  }, [foldState]);
  const { t } = useTranslate();
  const {
    itemName,
    imageUrl: [imageUrl] = [],
    externalItemId: spuId,
    internalItemId,
    skuInfo = [],
    itemUrl,
    itemSku,
  } = item;
  const storeStr = useStoreInfo(item);

  return (
    <div className={className} {...rest}>
      <div className="flex items-start">
        <div className="basis-auto overflow-hidden flex flex-grow">
          <div className="flex-shrink-0 !mr-[8px]">
            <Image hasSmallPic="both" src={imageUrl} width={60} height={60} />
          </div>
          <div className="basis-auto flex-grow overflow-hidden flex flex-col">
            <div className="flex items-center">
              <div
                title={itemName}
                className={cx(
                  'w-full group truncate break-all cursor-pointer leading-[20px] hover:text-[#3366ff]',
                  css`
                    &:hover {
                      + .anticon {
                        display: none;
                      }
                    }
                  `,
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(itemUrl, '_blank');
                }}
              >
                <LinkOutlined className="!hidden float-right bg-[var(--fett-chat-bubble-agent)] !text-[var(--fett-scenario-accent-text)] h-[20px] w-[20px] !leading-[20px] group-hover:!block" />
                {itemName}
              </div>
              <DeleteOutlined
                onClick={() => {
                  toggleSpu(internalItemId, true);
                }}
                className="cursor-pointer flex-shrink-0 ml-[4px]"
              />
            </div>
            <div className="h-[36px] mt-[4px] leading-[18px] text-[12px] text-[var(--fett-text-tertiary)]">
              <div className="truncate">
                {t('marketing.店铺')}: {storeStr}
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center truncate basis-auto overflow-hidden mr-[8px]">
                  <span>ID: {spuId}</span>
                  <div className="mx-[4px]">|</div>
                  <span className="truncate">
                    {t('aide.主商品货号')}: {itemSku || '-'}
                  </span>
                </div>
                {!!skuInfo?.length && (
                  <div
                    onClick={() => {
                      setOpen(open === 'fold' ? 'unfold' : 'fold');
                    }}
                    className="flex-shrink-0 cursor-pointer hover:text-[#3366ff]"
                  >
                    {t('knowledge.共{{x}}个SKU', { x: skuInfo?.length })}
                    {open === 'fold' ? (
                      <DownOutlined className="ml-[4px]" />
                    ) : (
                      <UpOutlined className="ml-[4px]" />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {open === 'unfold' && skuInfo.length > 0 && (
        <div className="mt-[8px]">
          <div className="border-0 border-t border-solid border-[var(--fett-border-default)] flex flex-wrap overflow-hidden gap-[0_16px]">
            {skuInfo.map((sku) => {
              return (
                <div
                  key={sku.skuId}
                  className="basis-[calc(50%-8px)] flex-shrink-0 flex-grow-0 mt-[9px] overflow-hidden flex justify-between items-center"
                >
                  <div
                    title={`${sku.sellerSku}(${sku.skuName})`}
                    className="w-full truncate break-all"
                  >
                    {sku.sellerSku}({sku.skuName})
                  </div>
                  <CloseCircleOutlined
                    onClick={() => {
                      toggleSku(internalItemId, sku.skuId, true);
                    }}
                    className="flex-shrink-0 cursor-pointer"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

ItemRender.displayName = 'ItemGroupItemRender';

const Image = styled(SafeImage)`
  width: 68px;
  height: 68px;
`;

interface TitleProps extends HTMLAttributes<HTMLDivElement> {
  text: ReactNode;
}

const Title = memo((props: TitleProps) => {
  const { text, className, ...restProps } = props;
  return (
    <div className={cx('h-[22px] flex items-center', className)} {...restProps}>
      <div className="h-[14px] w-[4px] bg-[#3370ff] mr-[8px]"></div>
      <div className="text-[16px] leading-[22px] text-[16px] font-medium">{text}</div>
    </div>
  );
});

Title.displayName = 'ItemGroupTitle';

EditDrawer.displayName = 'ItemGroupEditDrawer';
